import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import Modal from '../Modal/Modal';
import NamedLink from '../NamedLink/NamedLink';
import Avatar from '../Avatar/Avatar';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import curriculumConfig from '../../curriculum/curriculum_config.v1.0.json';
import { formatObjectiveWithStudentName } from '../../util/curriculumHelpers';
import { getCertificateDownloadUrl, getTermlySummaryDownloadUrl } from '../../util/api';
import { getCountryDialect } from '../../util/constants';
import ReactCountryFlag from 'react-country-flag';
import { GlobeIcon } from '../LearningPlacementModal/LearningPlacementModal';
import kpiBookOpen from '../../assets/book.gif';
import kpiTargetCenter from '../../assets/target.gif';
import kpiBarchartVertical from '../../assets/graph.gif';
import kpiFileChecklist from '../../assets/checklist.gif';
import iconSkillListening from '../../assets/skill-listening.png';
import iconSkillSpeaking from '../../assets/skill-speaking.png';
import iconSkillReading from '../../assets/skill-reading.png';
import iconSkillWriting from '../../assets/skill-writing.png';
import iconSkillScript from '../../assets/skill-script.png';
import css from './LearningProgress.module.css';

// Fast lookup map for curriculum objectives
const OBJECTIVE_MAP = new Map((curriculumConfig?.objectives || []).map(obj => [obj.id, obj]));

// Objective counts per level from curriculum framework
const CURRICULUM_TOTALS = {
  A1: { LIS: 6, SPK: 6, RD: 5, WR: 5, SC: 6 },
  A2: { LIS: 6, SPK: 6, RD: 5, WR: 5, SC: 5 },
  B1: { LIS: 5, SPK: 6, RD: 5, WR: 5, SC: 3 },
  B2: { LIS: 5, SPK: 5, RD: 5, WR: 5, SC: 0 },
};

const SKILL_CODE = {
  listening: 'LIS',
  speaking: 'SPK',
  reading: 'RD',
  writing: 'WR',
  script: 'SC',
};

function resolveCefrTier(skill, currentLevel) {
  if (currentLevel === 'Pre-A1') return 'A1';
  if (skill === 'script') {
    const map = {
      'Stage 0': 'A1',
      'Stage 1': 'A1',
      'Stage 2': 'A2',
      'Stage 3': 'B1',
      'Stage 4': 'B2',
    };
    return map[currentLevel] || 'A1';
  }
  return currentLevel;
}

function getCurriculumTotal(skill, currentLevel) {
  const tier = resolveCefrTier(skill, currentLevel);
  return CURRICULUM_TOTALS[tier]?.[SKILL_CODE[skill]] ?? 0;
}

const SKILL_METADATA = [
  { key: 'listening', title: 'Listening Comprehension' },
  { key: 'speaking', title: 'Speaking & Conversation' },
  { key: 'reading', title: 'Reading & Literacy' },
  { key: 'writing', title: 'Writing & Expression' },
  { key: 'script', title: 'Arabic Script Mastery' },
];

const SKILL_ICONS = {
  listening: iconSkillListening,
  speaking: iconSkillSpeaking,
  reading: iconSkillReading,
  writing: iconSkillWriting,
  script: iconSkillScript,
};

/* ── Normalize Skill Key to Canonical 5 CEFR Categories ── */
const normalizeSkillKey = skillKey => {
  if (!skillKey) return '';
  const s = String(skillKey).toLowerCase().trim();
  if (s.includes('listen')) return 'listening';
  if (s.includes('speak')) return 'speaking';
  if (s.includes('read')) return 'reading';
  if (s.includes('writ')) return 'writing';
  if (s.includes('script')) return 'script';
  return s;
};

/* ── Skill Icons matching Figma 7161:1949 ── */
const renderSkillIcon = (skillKey, customClass = css.skillSvg) => {
  const normKey = normalizeSkillKey(skillKey);
  const src = SKILL_ICONS[normKey];
  if (src) {
    return (
      <img
        src={src}
        alt={normKey}
        className={customClass}
        loading="lazy"
      />
    );
  }
  return (
    <IconCard
      type="learning_award"
      width="24"
      height="24"
      strokeWidth="2.2"
      className={customClass}
    />
  );
};

/* ── Decorative 3D Report Card Illustration exported directly from Figma 7161:2438 ── */
const DecorativeReportIllustration = () => (
  <div className={css.decorativeReportGraphic}>
    <svg width="120" height="132" viewBox="0 0 166 183" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="6.15506"
        y="38.5374"
        width="110.108"
        height="130.127"
        rx="16"
        transform="rotate(-7.17551 6.15506 38.5374)"
        fill="#F9FCFB"
      />
      <line
        x1="52.6737"
        y1="82.4943"
        x2="55.9558"
        y2="108.564"
        stroke="#D0E1DC"
        strokeWidth="11.25"
        strokeLinecap="round"
      />
      <line
        x1="71.3644"
        y1="70.683"
        x2="75.8186"
        y2="106.063"
        stroke="#B7D0C8"
        strokeWidth="11.25"
        strokeLinecap="round"
      />
      <line
        x1="90.055"
        y1="58.8716"
        x2="95.6814"
        y2="103.563"
        stroke="#8BB0A5"
        strokeWidth="11.25"
        strokeLinecap="round"
      />
      <line
        x1="42.4935"
        y1="134.85"
        x2="108.041"
        y2="126.598"
        stroke="#D0E1DC"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <line
        x1="43.7446"
        y1="144.782"
        x2="77.5113"
        y2="140.531"
        stroke="#D0E1DC"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <g filter="url(#filter0_d_7161_2438)">
        <rect
          x="17.4845"
          y="26.5946"
          width="110.108"
          height="130.127"
          rx="16"
          transform="rotate(-10 17.4845 26.5946)"
          fill="white"
        />
        <line
          x1="66.1127"
          y1="68.2056"
          x2="70.6755"
          y2="94.082"
          stroke="#D0E1DC"
          strokeWidth="11.25"
          strokeLinecap="round"
        />
        <line
          x1="84.1986"
          y1="55.4876"
          x2="90.3909"
          y2="90.6056"
          stroke="#B7D0C8"
          strokeWidth="11.25"
          strokeLinecap="round"
        />
        <line
          x1="102.285"
          y1="42.7696"
          x2="110.106"
          y2="87.1292"
          stroke="#8BB0A5"
          strokeWidth="11.25"
          strokeLinecap="round"
        />
        <line
          x1="58.5238"
          y1="121"
          x2="123.585"
          y2="109.528"
          stroke="#D0E1DC"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <line
          x1="60.2638"
          y1="130.858"
          x2="93.78"
          y2="124.948"
          stroke="#D0E1DC"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_7161_2438"
          x="-2.53507"
          y="-2.53519"
          width="171.07"
          height="187.309"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="10" />
          <feGaussianBlur stdDeviation="10" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7161_2438" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7161_2438" result="shape" />
        </filter>
      </defs>
    </svg>
  </div>
);

/**
 * LearningProgressView Component
 *
 * Reusable, executive-grade learning progress view shared between:
 * - /my-progress (Student Progress Page)
 * - /account/family (Family Dashboard Progress Tab)
 */
const LearningProgressView = ({
  learnerName = 'Learner',
  learningProfile = null,
  skillBars = [],
  learningStats = null,
  timelineReports = [],
  timelineHasMore = false,
  loadingTimeline = false,
  onLoadMoreTimeline,
  onOpenPlacementModal,
  onManageDisableScrolling,
  showHeroCard = true,
  showLevelHistory = true,
  isParentView = false,
  isIndependent = true,
  childSwitcherSlot = null,
  user = null,
  className,
}) => {
  const intl = useIntl();

  // Ensure onManageDisableScrolling is always a valid function to prevent ModalComponent crashes
  const handleManageDisableScrolling =
    typeof onManageDisableScrolling === 'function' ? onManageDisableScrolling : () => {};

  // Modals state
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [isHomeworkModalOpen, setIsHomeworkModalOpen] = useState(false);
  const [isLevelHistoryModalOpen, setIsLevelHistoryModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const isArabic = (intl?.locale || '').toLowerCase().startsWith('ar');

  const totalLessons = learningStats?.totalLessons ?? learningProfile?.totalLessons ?? 0;
  const totalObjectivesCompleted =
    learningStats?.totalObjectivesCompleted ?? learningProfile?.totalObjectivesCompleted ?? 0;
  const currentHomework = learningStats?.currentHomework || learningProfile?.currentHomework || '';

  const rawLearnerName =
    learnerName ||
    intl.formatMessage({
      id: 'MyProgressPage.defaultLearner',
      defaultMessage: 'Learner',
    });
  const learnerDisplayName = rawLearnerName.startsWith('STudent')
    ? 'Student' + rawLearnerName.slice(7)
    : rawLearnerName;

  const learnerInitials = (learnerDisplayName || 'L')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const reduxCurrentUser = useSelector(state => state.user?.currentUser);
  const activeUser = user || (!isParentView ? reduxCurrentUser : null);

  const hasAvatarImage = Boolean(
    activeUser?.profileImage && (activeUser.profileImage.id || activeUser.profileImage.attributes)
  );

  const skillsData = learningProfile?.skills || {};
  const hasProvisionalSkills = Object.values(skillsData).some(s => s.provisional === true);

  const b2Meta = learningProfile?.metadata?.b2Complete || {};
  const isB2Learner =
    skillsData.reading?.currentLevel === 'B2' ||
    skillsData.listening?.currentLevel === 'B2' ||
    Object.values(b2Meta).some(Boolean);

  // Calculate objectives for selected skill modal
  const selectedSkillObjectives = selectedSkill
    ? (curriculumConfig?.objectives || []).filter(o => {
        const skillCode = SKILL_CODE[selectedSkill.key];
        const tier = resolveCefrTier(selectedSkill.key, selectedSkill.currentLevel);
        return o.skill === skillCode && o.level === tier;
      })
    : [];

  const levelHistory = learningProfile?.levelHistory || [];

  const countryDialect = getCountryDialect(learningProfile?.dialect);
  const targetSearchQuery = countryDialect
    ? `?targetCountry=${encodeURIComponent(countryDialect.countryCode)}&pub_dialects=${encodeURIComponent(countryDialect.cluster.toLowerCase())}`
    : '';

  return (
    <div className={classNames(css.progressView, className)} dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Optional Child Switcher Slot (e.g. Family Dashboard) */}
      {childSwitcherSlot}

      {/* ── 1. Executive Hero Card ── */}
      {showHeroCard && (
        <div className={css.heroCard}>
          {/* Top Row: Learner Identity & Primary Hero CTA */}
          <div className={css.heroTopRow}>
            <div className={css.heroIdentity}>
              {hasAvatarImage ? (
                <Avatar
                  user={activeUser}
                  disableProfileLink
                  renderSizes="144px"
                  rootClassName={css.heroAvatar}
                />
              ) : (
                <div className={css.heroAvatar}>
                  <span className={css.heroAvatarInitials}>{learnerInitials}</span>
                </div>
              )}
              <div className={css.heroInfo}>
                <h1 className={css.heroTitle}>
                  <FormattedMessage
                    id="MyProgressPage.heroTitle"
                    values={{ name: learnerDisplayName }}
                    defaultMessage="{name}'s Arabic Journey"
                  />
                </h1>
                <p className={css.heroSubtitle}>
                  <FormattedMessage
                    id="MyProgressPage.heroSubtitle"
                    defaultMessage="Real-time mastery across all 5 CEFR competencies, calibrated through 1-on-1 native tutor sessions."
                  />
                </p>
              </div>
            </div>

            <div className={css.heroPrimaryCta}>
              <NamedLink
                name="SearchPage"
                to={{ search: targetSearchQuery }}
                className={css.primaryActionBtn}
              >
                <span>
                  <FormattedMessage
                    id="MyProgressPage.findTutorBtn"
                    defaultMessage="Find a Tutor"
                  />
                </span>
              </NamedLink>
            </div>
          </div>

          {/* Bottom Row: Metadata Badges & Secondary Utilities */}
          <div className={css.heroBottomRow}>
            <div className={css.heroBadges}>
              <span className={css.badgeMint}>
                <IconCard type="learning_user" width="12" height="12" strokeWidth="2.5" />
                {isParentView ? (
                  <FormattedMessage
                    id="MyProgressPage.badgeFamilyLearner"
                    defaultMessage="Family Learner"
                  />
                ) : (
                  <FormattedMessage
                    id="MyProgressPage.badgeIndependent"
                    defaultMessage="Independent Learner"
                  />
                )}
              </span>

              {learningProfile?.dialect && (
                <span className={css.badgeNeutral}>
                  <IconCard type="learning_location" width="12" height="12" strokeWidth="2.2" />
                  {(() => {
                    const cd = getCountryDialect(learningProfile.dialect);
                    if (cd) {
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                          {cd.countryCode && cd.countryCode !== 'MSA' ? (
                            <ReactCountryFlag
                              countryCode={cd.countryCode}
                              svg
                              style={{
                                width: '18px',
                                height: '13px',
                                borderRadius: '2px',
                                verticalAlign: 'middle',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                              }}
                              title={cd.countryName}
                            />
                          ) : (
                            <span style={{
                              width: '18px',
                              height: '13px',
                              borderRadius: '2px',
                              background: '#e6f4ea',
                              color: '#00674f',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              verticalAlign: 'middle',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                              flexShrink: 0,
                            }}>
                              <GlobeIcon size={11} />
                            </span>
                          )}
                          <span>{isArabic ? (cd.arabicCountryName || cd.arabicDialectName) : (cd.countryName || cd.dialectName)}</span>
                        </span>
                      );
                    }
                    return (
                      <FormattedMessage
                        id={`MyProgressPage.dialect.${learningProfile.dialect}`}
                        defaultMessage={learningProfile.dialect}
                      />
                    );
                  })()}
                </span>
              )}

              <span className={css.badgeNeutral}>
                <IconCard type="learning_shield" width="12" height="12" strokeWidth="2.2" />
                <FormattedMessage
                  id="MyProgressPage.badgeCefrTrack"
                  defaultMessage="Active CEFR Track"
                />
              </span>
            </div>

            <div className={css.heroSecondaryActions}>
              {totalLessons === 0 && onOpenPlacementModal && (
                <button type="button" className={css.retakeBtn} onClick={onOpenPlacementModal}>
                  <IconCard type="learning_refresh" width="13" height="13" strokeWidth="2.2" />
                  <span>
                    <FormattedMessage
                      id="MyProgressPage.retakePlacementBtn"
                      defaultMessage="Retake Placement"
                    />
                  </span>
                </button>
              )}

              <button
                type="button"
                className={css.downloadSummaryBtn}
                onClick={() => {
                  const url = getTermlySummaryDownloadUrl({
                    childId: learningProfile?.childId || user?.id?.uuid,
                    parentId: learningProfile?.parentId,
                    locale: isArabic ? 'ar' : 'en',
                  });
                  window.open(url, '_blank');
                }}
                title={intl.formatMessage({
                  id: 'MyProgressPage.downloadSummaryPdf',
                  defaultMessage: 'Download Progress Summary (PDF)',
                })}
              >
                <IconCard type="learning_file" width="13" height="13" strokeWidth="2.2" />
                <span>
                  <FormattedMessage
                    id="MyProgressPage.downloadSummaryPdf"
                    defaultMessage="Progress Summary (PDF)"
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 2. High-Impact KPI Metric Cards (4 Grid) ── */}
      <div className={css.metricsGrid}>
        {/* KPI 1: Lessons Completed */}
        <div className={css.metricCard}>
          <div className={css.kpiGlowMint} />
          <div className={css.metricCardTop}>
            <img src={kpiBookOpen} alt="Lessons Completed" className={css.kpiIconImg} />
          </div>
          <h3 className={css.metricLabel}>
            <FormattedMessage
              id="MyProgressPage.kpiLessonsCompleted"
              defaultMessage="Lessons Completed"
            />
          </h3>
          <div className={css.metricValue}>{totalLessons}</div>
          <p className={css.metricSub}>
            <FormattedMessage
              id="MyProgressPage.kpiLessonsSub"
              defaultMessage="1-on-1 native tutor sessions"
            />
          </p>
        </div>

        {/* KPI 2: Objectives Mastered */}
        <div className={css.metricCard}>
          <div className={css.kpiGlowPeach} />
          <div className={css.metricCardTop}>
            <img src={kpiTargetCenter} alt="Objectives Mastered" className={css.kpiIconImg} />
          </div>
          <h3 className={css.metricLabel}>
            <FormattedMessage
              id="MyProgressPage.kpiObjectivesMastered"
              defaultMessage="Objectives Mastered"
            />
          </h3>
          <div className={css.metricValue}>{totalObjectivesCompleted}</div>
          <p className={css.metricSub}>
            <FormattedMessage
              id="MyProgressPage.kpiObjectivesSub"
              defaultMessage="Curriculum milestones verified"
            />
          </p>
        </div>

        {/* KPI 3: Active Competencies */}
        <div className={css.metricCard}>
          <div className={css.kpiGlowLavender} />
          <div className={css.metricCardTop}>
            <img src={kpiBarchartVertical} alt="Competencies Tracked" className={css.kpiIconImg} />
          </div>
          <h3 className={css.metricLabel}>
            <FormattedMessage
              id="MyProgressPage.kpiCompetencies"
              defaultMessage="Competencies Tracked"
            />
          </h3>
          <div className={css.metricValue}>5 / 5</div>
          <p className={css.metricSub}>
            <FormattedMessage
              id="MyProgressPage.kpiCompetenciesSub"
              defaultMessage="Listening, Speaking, Reading, Writing, Arabic Script Mastery"
            />
          </p>
        </div>

        {/* KPI 4: Current Homework */}
        <div className={css.metricCard}>
          <div className={css.kpiGlowAmber} />
          <div className={css.metricCardTop}>
            <img src={kpiFileChecklist} alt="Current Homework" className={css.kpiIconImg} />
            {currentHomework && (
              <button
                type="button"
                className={css.viewAssignmentPill}
                onClick={() => setIsHomeworkModalOpen(true)}
              >
                <span>
                  <FormattedMessage
                    id="MyProgressPage.viewAssignmentBtn"
                    defaultMessage="View Assignment"
                  />
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            )}
          </div>
          <h3 className={css.metricLabel}>
            <FormattedMessage
              id="MyProgressPage.kpiCurrentHomework"
              defaultMessage="Current Homework"
            />
          </h3>
          <div className={css.metricValue}>
            {currentHomework ? (
              <FormattedMessage id="MyProgressPage.kpiActiveTask" defaultMessage="Active Task" />
            ) : (
              <FormattedMessage id="MyProgressPage.kpiUpToDate" defaultMessage="Up to Date" />
            )}
          </div>
          <p className={css.metricSub} title={currentHomework || ''}>
            {currentHomework || (
              <FormattedMessage
                id="MyProgressPage.kpiNoHomework"
                defaultMessage="All assignments completed"
              />
            )}
          </p>
        </div>
      </div>

      {/* ── 3. CEFR Skills Competency Benchmarks Section ── */}
      <div className={css.sectionHeader}>
        <div className={css.sectionTitleRow}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id="MyProgressPage.competencyTitle" />
          </h2>
          <p className={css.sectionSubtitle}>
            <FormattedMessage id="MyProgressPage.competencySubtitle" />
          </p>
        </div>
        <span className={css.sectionBadge}>
          <FormattedMessage id="MyProgressPage.competencyBadge" />
        </span>
      </div>

      {/* Provisional Notice Banner */}
      {hasProvisionalSkills && (
        <div className={css.provisionalAlert}>
          <IconCard
            type="learning_alertcircle"
            width="18"
            height="18"
            strokeWidth="2"
            className={css.provisionalAlertIcon}
          />
          <span>
            <FormattedMessage id="MyProgressPage.provisionalNotice" />
          </span>
        </div>
      )}

      {/* 5 Skills Cards Grid */}
      <div className={css.skillsGrid}>
        {SKILL_METADATA.map(meta => {
          // Script is retired once a student has reached B2
          if (meta.key === 'script' && isB2Learner) {
            return null;
          }

          const skillInfo = skillsData[meta.key] || {};
          const startingLevel = skillInfo.startingLevel || 'Pre-A1';
          const currentLevel = skillInfo.currentLevel || startingLevel;
          const isProvisional = skillInfo.provisional === true;

          const barData = skillBars.find(b => b.skill === meta.key);
          const completedCount =
            barData?.completedCount ?? (skillInfo.objectivesCompleted || []).length;
          const totalCount = barData?.totalCount ?? getCurriculumTotal(meta.key, currentLevel);
          const percent =
            totalCount > 0
              ? barData?.percent ?? Math.min(100, Math.round((completedCount / totalCount) * 100))
              : 0;

          const isAdvancementReady = percent >= 80 || barData?.isCeiling;

          return (
            <div key={meta.key} className={css.skillCard}>
              <div className={css.skillCardHeader}>
                <div className={css.skillTitleArea}>
                  <div className={css.skillIconBox}>{renderSkillIcon(meta.key)}</div>
                  <span className={css.skillName}>
                    <FormattedMessage
                      id={`MyProgressPage.skill.${meta.key}`}
                      defaultMessage={meta.title}
                    />
                  </span>
                </div>

                <div className={css.levelBadgesRow}>
                  <span className={css.levelPill}>
                    {currentLevel === 'Pre-A1' ? (
                      <FormattedMessage id="MyProgressPage.levelGettingStarted" />
                    ) : (
                      currentLevel
                    )}
                  </span>
                  {isProvisional && (
                    <span className={css.levelPillProvisional}>
                      <FormattedMessage id="MyProgressPage.provisionalPill" />
                    </span>
                  )}
                </div>
              </div>

              {/* Starting Baseline & Progress Indicator (Figma Frame 1984082502) */}
              <div className={css.baselineBox}>
                <div className={css.baselineLeft}>
                  <span className={css.baselineLabel}>
                    <FormattedMessage
                      id="MyProgressPage.baselineLabel"
                      defaultMessage="STARTING BASELINE:"
                    />
                  </span>
                  <span className={css.baselineValue}>
                    {startingLevel === 'Pre-A1' ? (
                      <FormattedMessage
                        id="MyProgressPage.levelPreA1"
                        defaultMessage="Pre-A1 (Getting started)"
                      />
                    ) : (
                      startingLevel
                    )}
                  </span>
                </div>
                <div className={css.baselineDivider} />
                <div className={css.baselineRight}>
                  <div className={css.baselineProgressBars}>
                    <span className={css.baselineBar1} />
                    <span className={css.baselineBar2} />
                    <span className={css.baselineBar3} />
                  </div>
                  <span className={css.baselineProgressLabel}>
                    <FormattedMessage
                      id="MyProgressPage.yourProgress"
                      defaultMessage="YOUR PROGRESS"
                    />
                  </span>
                </div>
              </div>

              {/* Progress Track */}
              <div className={css.progressContainer}>
                <div className={css.progressInfoRow}>
                  <span className={css.progressInfoLabel}>
                    <FormattedMessage
                      id="MyProgressPage.curriculumObjectives"
                      defaultMessage="Curriculum Objectives"
                    />
                  </span>
                  <div className={css.progressInfoRight}>
                    <span className={css.progressCountText}>
                      {completedCount} / {totalCount}
                    </span>
                    <span className={css.progressPercentBadge}>{percent}%</span>
                  </div>
                </div>

                <div className={css.progressTrack}>
                  <div
                    className={css.progressFill}
                    style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
                  />
                </div>

                <div className={css.milestoneRow}>
                  {isAdvancementReady ? (
                    <span className={css.advancementReady}>
                      <IconCard type="learning_check" width="14" height="14" strokeWidth="2.5" />
                      <FormattedMessage
                        id="MyProgressPage.advancementReady"
                        defaultMessage="Eligible for level advancement"
                      />
                    </span>
                  ) : (
                    <span className={css.milestoneRemainingText}>
                      {totalCount > completedCount ? (
                        <FormattedMessage
                          id="MyProgressPage.objectivesRemaining"
                          values={{ count: totalCount - completedCount }}
                          defaultMessage="{count} objectives to next milestone"
                        />
                      ) : (
                        <FormattedMessage
                          id="MyProgressPage.tierCompleted"
                          defaultMessage="Completed"
                        />
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* Trigger Modal to View All Curriculum Objectives */}
              <button
                type="button"
                className={css.viewObjectivesPillBtn}
                onClick={() =>
                  setSelectedSkill({
                    key: meta.key,
                    title: meta.title,
                    currentLevel,
                    startingLevel,
                    completedCount,
                    totalCount,
                    percent,
                    isProvisional,
                    completedObjectives: skillInfo.objectivesCompleted || [],
                  })
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <circle cx="4" cy="6" r="1.5" fill="currentColor" />
                  <circle cx="4" cy="12" r="1.5" fill="currentColor" />
                  <circle cx="4" cy="18" r="1.5" fill="currentColor" />
                </svg>
                <span>
                  <FormattedMessage
                    id="MyProgressPage.viewObjectivesBtn"
                    values={{ completed: completedCount, total: totalCount }}
                    defaultMessage="View Objectives ({completed}/{total})"
                  />
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {/* ── 4. Permanent Level History Audit Trail (Family & Admin/History View) ── */}
      {showLevelHistory && (
        <div className={css.historyCard}>
          <div className={css.sectionHeader}>
            <div className={css.sectionTitleRow}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="MyProgressPage.levelHistoryTitle" />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="MyProgressPage.levelHistorySubtitle" />
              </p>
            </div>
            <div className={css.historyHeaderActions}>
              <span className={css.sectionBadge}>
                <FormattedMessage
                  id="MyProgressPage.levelMilestonesCount"
                  values={{ count: levelHistory.length }}
                />
              </span>
              <button
                type="button"
                className={css.viewHistoryBtn}
                onClick={() => {
                  setSelectedMilestone(null);
                  setIsLevelHistoryModalOpen(true);
                }}
              >
                <span>
                  <FormattedMessage id="MyProgressPage.viewAuditTrailBtn" />
                </span>
                <IconCard type="arrowRight" width="14" height="14" strokeWidth="2.5" />
              </button>
            </div>
          </div>

          {levelHistory.length === 0 ? (
            <div className={css.historyEmpty}>
              <FormattedMessage id="MyProgressPage.noLevelHistory" />
            </div>
          ) : (
            <div className={css.historyList}>
              {levelHistory.slice(0, 2).map((item, idx) => {
                const dateStr = item.date
                  ? new Date(item.date).toLocaleDateString(isArabic ? 'ar-EG' : undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : null;

                const isInitial =
                  item.confirmedBy === 'placement_questionnaire' ||
                  item.confirmedBy === 'placement' ||
                  !item.fromLevel ||
                  (item.reason &&
                    (item.reason.toLowerCase().includes('placement questionnaire') ||
                      item.reason.toLowerCase().includes('initial placement')));

                return (
                  <div
                    key={idx}
                    className={`${css.historyItem} ${css.historyItemClickable}`}
                    onClick={() => {
                      setSelectedMilestone(item);
                      setIsLevelHistoryModalOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedMilestone(item);
                        setIsLevelHistoryModalOpen(true);
                      }
                    }}
                  >
                    <div className={css.historyIcon}>
                      {renderSkillIcon(item.skill, css.historySkillSvg)}
                    </div>
                    <div className={css.historyDetails}>
                      <span className={css.historyTitle}>
                        {item.skill ? (
                          <FormattedMessage
                            id={`MyProgressPage.skill.${item.skill.toLowerCase()}`}
                            defaultMessage={item.skill}
                          />
                        ) : (
                          item.skill
                        )}
                        : {item.fromLevel || <FormattedMessage id="MyProgressPage.initialLevel" />}{' '}
                        → {item.toLevel}
                      </span>
                      {!isInitial && (
                        <span className={css.historyMeta}>
                          <FormattedMessage
                            id="MyProgressPage.confirmedByTutor"
                            values={{
                              tutor: item.tutorId ? `(${item.tutorId.substring(0, 8)}...)` : '',
                            }}
                          />
                          {item.reason ? ` • ${item.reason}` : ''}
                          {item.completedObjectives?.length > 0 && (
                            <>
                              {' • '}
                              <FormattedMessage
                                id="MyProgressPage.objectivesCountMastered"
                                values={{ count: item.completedObjectives.length }}
                              />
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <div className={css.historyItemRight}>
                      {dateStr && <span className={css.historyDate}>{dateStr}</span>}
                      {!isInitial && (
                        <button
                          type="button"
                          className={css.downloadCertBtn}
                          onClick={e => {
                            e.stopPropagation();
                            const url = getCertificateDownloadUrl({
                              certificateId: item.certificateId,
                              childId: learningProfile?.childId,
                              skill: item.skill,
                              level: item.toLevel,
                              locale: isArabic ? 'ar' : 'en',
                            });
                            window.open(url, '_blank');
                          }}
                          title={intl.formatMessage({
                            id: 'MyProgressPage.downloadCertificate',
                            defaultMessage: 'Download Certificate',
                          })}
                        >
                          <IconCard
                            type="learning_award"
                            width="13"
                            height="13"
                            strokeWidth="2.2"
                          />
                          <span>
                            <FormattedMessage
                              id="MyProgressPage.downloadCertificate"
                              defaultMessage="Certificate"
                            />
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {levelHistory.length > 0 && (
                <div className={css.historyBottomPillRow}>
                  <button
                    type="button"
                    className={css.viewAllMilestonesPillBtn}
                    onClick={() => {
                      setSelectedMilestone(null);
                      setIsLevelHistoryModalOpen(true);
                    }}
                  >
                    <span>
                      <FormattedMessage
                        id="MyProgressPage.viewAllMilestonesBtn"
                        values={{ count: levelHistory.length }}
                        defaultMessage="View all {count} milestones in audit trail"
                      />
                    </span>
                    <IconCard type="arrowRight" width="14" height="14" strokeWidth="2.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 5. Lesson History & Tutor Evaluations Timeline ── */}
      <div className={css.timelineSection}>
        <div className={css.sectionHeader}>
          <div className={css.sectionTitleRow}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="MyProgressPage.timelineTitle" />
            </h2>
            <p className={css.sectionSubtitle}>
              <FormattedMessage id="MyProgressPage.timelineSubtitle" />
            </p>
          </div>

          {timelineReports.length > 0 && (
            <span className={css.sectionBadge}>
              <FormattedMessage
                id="MyProgressPage.reportsCount"
                values={{ count: timelineReports.length }}
              />
            </span>
          )}
        </div>

        {/* Timeline Summary Cards (Click to Open Full Modal) */}
        {timelineReports.length > 0 && (
          <div className={css.timelineList}>
            {timelineReports.map((report, idx) => {
              const dateStr =
                report.submittedAt || report.createdAt
                  ? new Date(report.submittedAt || report.createdAt).toLocaleDateString(
                      isArabic ? 'ar-EG' : undefined,
                      {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }
                    )
                  : null;

              const isPlacement = report.type === 'placement';
              const hasLevelUps = report.levelUps && report.levelUps.length > 0;
              const objectives = report.objectivesCovered || [];

              return (
                <div key={report._id || report.id || idx} className={css.reportSummaryCard}>
                  <div className={css.reportSummaryLeft}>
                    <div className={css.reportHeader}>
                      <span className={css.reportMetaItem}>
                        <IconCard type="learning_calendar" width="16" height="16" strokeWidth="2" />
                        <span>{dateStr || <FormattedMessage id="MyProgressPage.recentLesson" />}</span>
                      </span>

                      {report.dialect && (
                        <>
                          <span className={css.reportMetaDivider} />
                          <span className={css.reportMetaItem}>
                            <IconCard type="learning_bookopen" width="16" height="16" strokeWidth="2" />
                            <span>
                              <FormattedMessage
                                id={`MyProgressPage.dialect.${report.dialect}`}
                                defaultMessage={report.dialect}
                              />
                            </span>
                          </span>
                        </>
                      )}

                      <span className={css.reportMetaDivider} />

                      <span
                        className={classNames(
                          css.reportTypeTag,
                          isPlacement ? css.reportTypePlacement : css.reportTypeLesson
                        )}
                      >
                        <IconCard
                          type={isPlacement ? 'learning_shield' : 'learning_bookopen'}
                          width="16"
                          height="16"
                          strokeWidth="2"
                        />
                        <span>
                          {isPlacement ? (
                            <FormattedMessage id="MyProgressPage.typePlacement" />
                          ) : (
                            <FormattedMessage id="MyProgressPage.typeLesson" />
                          )}
                        </span>
                      </span>

                      {report.tutorName && (
                        <>
                          <span className={css.reportMetaDivider} />
                          <span className={css.reportMetaItem}>
                            <IconCard type="learning_user" width="16" height="16" strokeWidth="2" />
                            <span>
                              <FormattedMessage
                                id="MyProgressPage.tutorPrefix"
                                values={{ name: report.tutorName }}
                              />
                            </span>
                          </span>
                        </>
                      )}
                    </div>

                    <div className={css.reportSummaryPills}>
                      {isPlacement ? (
                        <span className={css.objectivesPill}>
                          <IconCard type="learning_shield" width="16" height="16" strokeWidth="2" />
                          <span>
                            <FormattedMessage id="MyProgressPage.placementBaselinePill" />
                          </span>
                        </span>
                      ) : (
                        <>
                          {objectives.length > 0 && (
                            <span className={css.objectivesPill}>
                              <IconCard
                                type="learning_check"
                                width="16"
                                height="16"
                                strokeWidth="2.5"
                              />
                              <span>
                                <FormattedMessage
                                  id="MyProgressPage.objectivesMasteredCount"
                                  values={{ count: objectives.length }}
                                />
                              </span>
                            </span>
                          )}

                          {hasLevelUps && (
                            <span className={css.levelUpPill}>
                              <IconCard
                                type="learning_award"
                                width="16"
                                height="16"
                                strokeWidth="2"
                              />
                              <span>
                                <FormattedMessage id="MyProgressPage.levelUpAchieved" />
                              </span>
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {isPlacement ? (
                      <p className={css.reportPreviewSnippet}>
                        <FormattedMessage id="MyProgressPage.placementTimelineSnippet" />
                      </p>
                    ) : (
                      report.strengths && (
                        <p className={css.reportPreviewSnippet}>
                          <span className={css.strengthsPrefix}>
                            <FormattedMessage id="MyProgressPage.strengthsLabel" defaultMessage="Strengths: " />
                          </span>
                          <span>{report.strengths}</span>
                        </p>
                      )
                    )}
                  </div>

                  <div className={css.reportCardDivider} />

                  <div className={css.reportSummaryRight}>
                    <DecorativeReportIllustration />
                    {/* Button Triggering the Full Report Modal */}
                    <button
                      type="button"
                      className={css.viewReportBtn}
                      onClick={() => setSelectedReport(report)}
                    >
                      <span>
                        <FormattedMessage
                          id="MyProgressPage.viewFullReportBtn"
                          defaultMessage="View Full Report"
                        />
                      </span>
                      <IconCard type="arrowRight" width="16" height="16" strokeWidth="2.5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Load More Button */}
            {timelineHasMore && (
              <div className={css.loadMoreContainer}>
                <button
                  type="button"
                  className={css.loadMoreBtn}
                  onClick={onLoadMoreTimeline}
                  disabled={loadingTimeline}
                >
                  {loadingTimeline ? (
                    <FormattedMessage id="MyProgressPage.loadingOlderReports" />
                  ) : (
                    <FormattedMessage id="MyProgressPage.loadOlderReportsBtn" />
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State when 0 lessons have been attended yet */}
        {timelineReports.length === 0 && (
          <div className={css.noLessonsCard}>
            <div className={css.noLessonsLeft}>
              <div className={css.noLessonsIconCircle}>
                <IconCard type="learning_bookopen" width="26" height="26" strokeWidth="2" />
              </div>
              <div className={css.noLessonsInfo}>
                <h3 className={css.noLessonsTitle}>
                  <FormattedMessage id="MyProgressPage.emptyTimelineTitle" />
                </h3>
                <p className={css.noLessonsDesc}>
                  <FormattedMessage id="MyProgressPage.emptyTimelineDesc" />
                </p>
              </div>
            </div>

            <NamedLink
              name="SearchPage"
              to={{ search: targetSearchQuery }}
              className={css.primaryActionBtn}
            >
              <span>
                <FormattedMessage id="MyProgressPage.findTutorBtn" />
              </span>
              <IconCard type="arrowRight" width="14" height="14" strokeWidth="2.5" />
            </NamedLink>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          MODAL 1: Lesson Report Details Modal
          ══════════════════════════════════════════════════════════ */}
      <Modal
        id="LessonReportDetailsModal"
        isOpen={Boolean(selectedReport)}
        onClose={() => setSelectedReport(null)}
        onManageDisableScrolling={handleManageDisableScrolling}
        usePortal
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        {selectedReport && (
          <div dir={isArabic ? 'rtl' : 'ltr'}>
            <div className={css.modalHeader}>
              <div className={css.modalHeaderTop}>
                <span
                  className={`${css.reportTypeTag} ${
                    selectedReport.type === 'placement'
                      ? css.reportTypePlacement
                      : css.reportTypeLesson
                  }`}
                >
                  {selectedReport.type === 'placement' ? (
                    <FormattedMessage id="MyProgressPage.typePlacement" />
                  ) : (
                    <FormattedMessage id="MyProgressPage.typeLesson" />
                  )}
                </span>

                {(selectedReport.dialect || selectedReport.type === 'placement' || learningProfile?.dialect) && (
                  <span className={css.reportDialectTag}>
                    <FormattedMessage
                      id={`MyProgressPage.dialect.${(selectedReport.dialect || learningProfile?.dialect || 'gulf').toLowerCase()}`}
                      defaultMessage={selectedReport.dialect || learningProfile?.dialect || 'Gulf Dialect'}
                    />
                  </span>
                )}

                <span className={css.reportMetaItem}>
                  <IconCard type="learning_calendar" width="13" height="13" strokeWidth="2" />
                  {selectedReport.submittedAt || selectedReport.createdAt ? (
                    new Date(
                      selectedReport.submittedAt || selectedReport.createdAt
                    ).toLocaleDateString(isArabic ? 'ar-EG' : undefined, {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  ) : (
                    <FormattedMessage id="MyProgressPage.recentSession" />
                  )}
                </span>

                {selectedReport.tutorName && (
                  <span className={css.reportMetaItem}>
                    <IconCard type="learning_user" width="13" height="13" strokeWidth="2" />
                    <FormattedMessage
                      id="MyProgressPage.tutorPrefix"
                      values={{ name: selectedReport.tutorName }}
                    />
                  </span>
                )}
              </div>

              <h2
                className={`${css.modalTitle} ${
                  selectedReport.type === 'placement' ? css.placementModalTitle : ''
                }`}
              >
                {selectedReport.type === 'placement' ? (
                  <FormattedMessage id="MyProgressPage.modalPlacementTitle" />
                ) : (
                  <FormattedMessage id="MyProgressPage.modalReportTitle" />
                )}
              </h2>
              <p
                className={`${css.modalSubtitle} ${
                  selectedReport.type === 'placement' ? css.placementModalSubtitle : ''
                }`}
              >
                {selectedReport.type === 'placement' ? (
                  <FormattedMessage id="MyProgressPage.modalPlacementSubtitle" />
                ) : (
                  <FormattedMessage id="MyProgressPage.modalReportSubtitle" />
                )}
              </p>
            </div>

            <div className={css.modalBody}>
              {selectedReport.type === 'placement' ? (
                /* ── Placement Assessment Benchmark View ── */
                <div className={css.placementModalBody}>
                  <div className={css.placementHeroBanner}>
                    <div className={css.placementHeroIconCircle}>
                      <IconCard type="learning_shield" width="20" height="20" strokeWidth="2" />
                    </div>
                    <div className={css.placementHeroText}>
                      <h4 className={css.placementHeroHeading}>
                        <FormattedMessage id="MyProgressPage.placementOverviewTitle" />
                      </h4>
                      <p className={css.placementHeroDesc}>
                        <FormattedMessage id="MyProgressPage.placementOverviewDesc" />
                      </p>
                    </div>
                  </div>

                  <div className={css.placementSkillsSection}>
                    <h4 className={css.placementSkillsHeading}>
                      <FormattedMessage id="MyProgressPage.calibratedSkillsHeading" />
                    </h4>
                    <div className={css.placementSkillsGrid}>
                      {SKILL_METADATA.map(meta => {
                        const snapSkill = selectedReport.snapshot?.[meta.key];
                        const profSkill = skillsData[meta.key];
                        const skillLevel =
                          snapSkill?.level ||
                          profSkill?.startingLevel ||
                          profSkill?.currentLevel ||
                          'Pre-A1';
                        const isProvisional =
                          snapSkill?.provisional ?? profSkill?.provisional ?? true;

                        return (
                          <div key={meta.key} className={css.placementSkillItem}>
                            <div className={css.placementSkillLeft}>
                              <div className={css.placementSkillIconBox}>
                                {renderSkillIcon(meta.key, css.placementSkillSvg)}
                              </div>
                              <div className={css.placementSkillInfo}>
                                <span className={css.placementSkillName}>
                                  <FormattedMessage
                                    id={`MyProgressPage.skill.${meta.key}`}
                                    defaultMessage={meta.title}
                                  />
                                </span>
                                <span className={css.placementSkillStatus}>
                                  {isProvisional ? (
                                    <FormattedMessage id="MyProgressPage.provisionalBaselinePill" />
                                  ) : (
                                    <FormattedMessage id="MyProgressPage.confirmedBaselinePill" />
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className={css.placementSkillRight}>
                              <span className={css.placementLevelBadge}>{skillLevel}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={css.placementNextStepsBox}>
                    <div className={css.placementNextStepsTop}>
                      <IconCard type="learning_alertcircle" width="18" height="18" strokeWidth="2" />
                      <span className={css.placementNextStepsTitle}>
                        <FormattedMessage id="MyProgressPage.placementNextStepsTitle" />
                      </span>
                    </div>
                    <p className={css.placementNextStepsText}>
                      <FormattedMessage id="MyProgressPage.placementNextStepsText" />
                    </p>
                  </div>
                </div>
              ) : (
                /* ── Standard Lesson Evaluation View ── */
                <>
                  {/* Celebratory Level-Up Banner if applicable */}
                  {selectedReport.levelUps && selectedReport.levelUps.length > 0 && (
                    <div className={css.levelUpBanner}>
                      <div className={css.levelUpIconCircle}>
                        <IconCard type="learning_award" width="18" height="18" strokeWidth="2" />
                      </div>
                      <div className={css.levelUpContent}>
                        <h4 className={css.levelUpHeading}>
                          <FormattedMessage id="MyProgressPage.modalLevelUpHeading" />
                        </h4>
                        {selectedReport.levelUps.map((lu, lIdx) => (
                          <div key={lIdx} className={css.levelUpDescRow}>
                            <p className={css.levelUpDesc}>
                              <FormattedMessage
                                id="MyProgressPage.modalLevelUpDesc"
                                values={{
                                  skill: <strong>{lu.skill}</strong>,
                                  from: <span className={css.levelBadgeMini}>{lu.fromLevel}</span>,
                                  to: <span className={css.levelBadgeMini}>{lu.toLevel}</span>,
                                }}
                              />
                            </p>
                            <button
                              type="button"
                              className={css.downloadCertMiniBtn}
                              onClick={() => {
                                const url = getCertificateDownloadUrl({
                                  certificateId: lu.certificateId,
                                  childId: learningProfile?.childId,
                                  skill: lu.skill,
                                  level: lu.toLevel,
                                  locale: isArabic ? 'ar' : 'en',
                                });
                                window.open(url, '_blank');
                              }}
                              title={intl.formatMessage({
                                id: 'MyProgressPage.downloadCertificate',
                                defaultMessage: 'Download Certificate',
                              })}
                            >
                              <IconCard
                                type="learning_award"
                                width="12"
                                height="12"
                                strokeWidth="2.2"
                              />
                              <span>
                                <FormattedMessage
                                  id="MyProgressPage.downloadCertificate"
                                  defaultMessage="Certificate"
                                />
                              </span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Objectives Covered */}
                  {selectedReport.objectivesCovered && selectedReport.objectivesCovered.length > 0 && (
                    <div className={css.objectivesGroup}>
                      <h4 className={css.objectivesHeading}>
                        <FormattedMessage
                          id="MyProgressPage.modalObjectivesHeading"
                          values={{ count: selectedReport.objectivesCovered.length }}
                        />
                      </h4>
                      <div className={css.objectivesListModal}>
                        {selectedReport.objectivesCovered.map((obj, oIdx) => {
                          const objId = obj.objectiveId || obj;
                          const curriculumObj = OBJECTIVE_MAP.get(objId);
                          const rawText = isArabic
                            ? curriculumObj?.text_ar || curriculumObj?.text_en
                            : curriculumObj?.text_en || curriculumObj?.text_ar;
                          const objText = formatObjectiveWithStudentName(
                            rawText,
                            learnerDisplayName,
                            isArabic
                          );

                          return (
                            <div
                              key={oIdx}
                              className={`${css.objectiveRow} ${css.objectiveRowCompleted}`}
                            >
                              <div className={css.objectiveCheckCircle}>
                                <IconCard
                                  type="learning_check"
                                  width="12"
                                  height="12"
                                  strokeWidth="3"
                                />
                              </div>
                              <div className={css.objectiveTextGroup}>
                                <div className={css.objectiveCodeRow}>
                                  <span className={css.objectiveCode}>{objId}</span>
                                  <span
                                    className={`${css.objectiveStatusTag} ${css.objectiveStatusCompleted}`}
                                  >
                                    <FormattedMessage id="MyProgressPage.statusMastered" />
                                  </span>
                                </div>
                                <p className={css.objectiveDescription}>
                                  {objText || (
                                    <FormattedMessage id="MyProgressPage.defaultObjectiveDesc" />
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Tutor Feedback 2x2 Grid */}
                  <div className={css.feedbackGrid}>
                    {selectedReport.strengths && (
                      <div className={`${css.feedbackBox} ${css.feedbackStrengths}`}>
                        <div className={css.feedbackBoxTop}>
                          <div className={`${css.feedbackIconBox} ${css.iconBoxStrengths}`}>
                            <IconCard type="learning_star" width="16" height="16" strokeWidth="2" />
                          </div>
                          <span className={`${css.feedbackBoxTitle} ${css.titleStrengths}`}>
                            <FormattedMessage id="MyProgressPage.strengthsDemonstrated" />
                          </span>
                        </div>
                        <p className={css.feedbackText}>{selectedReport.strengths}</p>
                      </div>
                    )}

                    {selectedReport.needsWork && (
                      <div className={`${css.feedbackBox} ${css.feedbackNeedsWork}`}>
                        <div className={css.feedbackBoxTop}>
                          <div className={`${css.feedbackIconBox} ${css.iconBoxNeedsWork}`}>
                            <IconCard
                              type="learning_target"
                              width="16"
                              height="16"
                              strokeWidth="2"
                            />
                          </div>
                          <span className={`${css.feedbackBoxTitle} ${css.titleNeedsWork}`}>
                            <FormattedMessage id="MyProgressPage.needsWorkFocus" />
                          </span>
                        </div>
                        <p className={css.feedbackText}>{selectedReport.needsWork}</p>
                        <div className={css.feedbackBottomPillRow}>
                          <span className={`${css.feedbackPill} ${css.pillNeedsWork}`}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <FormattedMessage id="MyProgressPage.visibleToTutors" />
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedReport.homework && (
                      <div className={`${css.feedbackBox} ${css.feedbackHomework}`}>
                        <div className={css.feedbackBoxTop}>
                          <div className={`${css.feedbackIconBox} ${css.iconBoxHomework}`}>
                            <IconCard type="learning_homework" width="16" height="16" strokeWidth="2" />
                          </div>
                          <span className={`${css.feedbackBoxTitle} ${css.titleHomework}`}>
                            <FormattedMessage id="MyProgressPage.assignedHomework" />
                          </span>
                        </div>
                        <p className={css.feedbackText}>{selectedReport.homework}</p>
                      </div>
                    )}

                    {(selectedReport.noteToParent || selectedReport.noteToStudent) && (
                      <div className={`${css.feedbackBox} ${css.feedbackNote}`}>
                        <div className={css.feedbackBoxTop}>
                          <div className={`${css.feedbackIconBox} ${css.iconBoxNote}`}>
                            <IconCard
                              type="learning_message"
                              width="16"
                              height="16"
                              strokeWidth="2"
                            />
                          </div>
                          <span className={`${css.feedbackBoxTitle} ${css.titleNote}`}>
                            <FormattedMessage id="MyProgressPage.noteFromTutor" />
                          </span>
                        </div>
                        <p className={css.feedbackText}>
                          {selectedReport.noteToParent || selectedReport.noteToStudent}
                        </p>
                        <div className={css.feedbackBottomPillRow}>
                          <span className={`${css.feedbackPill} ${css.pillNote}`}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <FormattedMessage id="MyProgressPage.privatePill" />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Fallback if no objectives or written feedback were recorded */}
                  {!selectedReport.strengths &&
                    !selectedReport.needsWork &&
                    !selectedReport.homework &&
                    !selectedReport.noteToParent &&
                    !selectedReport.noteToStudent &&
                    (!selectedReport.objectivesCovered ||
                      selectedReport.objectivesCovered.length === 0) &&
                    (!selectedReport.levelUps || selectedReport.levelUps.length === 0) && (
                      <div className={css.emptyFeedbackNotice}>
                        <IconCard type="learning_info" width="18" height="18" strokeWidth="2" />
                        <p>
                          <FormattedMessage id="MyProgressPage.noWrittenFeedback" />
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>

            <div className={css.modalFooter}>
              <button
                type="button"
                className={css.modalCloseBtn}
                onClick={() => setSelectedReport(null)}
              >
                <FormattedMessage id="MyProgressPage.closeBtn" />
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 2: Skill Curriculum Objectives Breakdown Modal (Figma 7161:2525)
          ══════════════════════════════════════════════════════════ */}
      <Modal
        id="SkillObjectivesModal"
        isOpen={Boolean(selectedSkill)}
        onClose={() => setSelectedSkill(null)}
        onManageDisableScrolling={handleManageDisableScrolling}
        usePortal
        containerClassName={css.skillModalContainer}
        contentClassName={css.modalContent}
      >
        {selectedSkill && (
          <div dir={isArabic ? 'rtl' : 'ltr'}>
            <div className={css.skillModalHeader}>
              <div className={css.skillModalHeaderTop}>
                <span className={css.skillModalLevelPill}>
                  {selectedSkill.currentLevel === 'Pre-A1' ? (
                    <FormattedMessage id="MyProgressPage.levelGettingStarted" defaultMessage="Pre-A1" />
                  ) : (
                    selectedSkill.currentLevel
                  )}
                </span>
                {selectedSkill.isProvisional && (
                  <span className={css.levelPillProvisional}>
                    <FormattedMessage id="MyProgressPage.provisionalPill" />
                  </span>
                )}
                <span className={css.skillModalCefrPill}>
                  <FormattedMessage id="MyProgressPage.cefrFramework" defaultMessage="CEFR Framework" />
                </span>
              </div>

              <h2 className={css.skillModalTitle}>
                <FormattedMessage
                  id={`MyProgressPage.skill.${selectedSkill.key}`}
                  defaultMessage={selectedSkill.title}
                />{' '}
                — {selectedSkill.currentLevel === 'Pre-A1' ? 'Pre-A1' : selectedSkill.currentLevel}
              </h2>
              <p className={css.skillModalSubtitle}>
                <FormattedMessage
                  id="MyProgressPage.skillModalSubtitle"
                  defaultMessage="Official curriculum milestones for {level} with real-time mastery verification."
                  values={{ level: selectedSkill.currentLevel }}
                />
              </p>
            </div>

            <div className={css.modalBody}>
              {/* Mastery Progress Card inside Modal */}
              <div className={css.skillModalProgressBox}>
                <div className={css.progressInfoRow}>
                  <span className={css.skillModalProgressLabel}>
                    <FormattedMessage id="MyProgressPage.tierMastery" defaultMessage="Tier Mastery" />
                  </span>
                  <span className={css.skillModalProgressValue}>
                    {selectedSkill.completedCount} / {selectedSkill.totalCount} (
                    {selectedSkill.percent}%)
                  </span>
                </div>

                <div className={css.skillModalProgressTrack}>
                  <div
                    className={css.skillModalProgressFill}
                    style={{ width: `${Math.min(100, Math.max(0, Math.round(selectedSkill.percent)))}%` }}
                  />
                </div>
              </div>

              {/* Complete Objectives List for this Tier */}
              <div className={css.objectivesGroup}>
                <h4 className={css.skillModalObjectivesHeading}>
                  <FormattedMessage
                    id="MyProgressPage.milestoneObjectivesHeading"
                    defaultMessage="Curriculum Milestone Objectives ({count})"
                    values={{ count: selectedSkillObjectives.length }}
                  />
                </h4>

                <div className={css.objectivesListModal}>
                  {selectedSkillObjectives.map((obj, oIdx) => {
                    const isAchieved = (selectedSkill.completedObjectives || []).some(
                      id => id === obj.id || id?.objectiveId === obj.id
                    );

                    const rawText = isArabic
                      ? obj.text_ar || obj.text_en
                      : obj.text_en || obj.text_ar;
                    const objText = formatObjectiveWithStudentName(
                      rawText,
                      learnerDisplayName,
                      isArabic
                    );

                    return (
                      <div
                        key={obj.id || oIdx}
                        className={`${css.objectiveRow} ${
                          isAchieved ? css.objectiveRowCompleted : css.objectiveRowPending
                        }`}
                      >
                        {isAchieved ? (
                          <div className={css.objectiveCheckCircle}>
                            <IconCard
                              type="learning_check"
                              width="12"
                              height="12"
                              strokeWidth="3"
                            />
                          </div>
                        ) : (
                          <div className={css.objectivePendingCircle} />
                        )}

                        <div className={css.objectiveTextGroup}>
                          <div className={css.objectiveCodeRow}>
                            <span className={css.objectiveCode}>{obj.id}</span>
                            <span
                              className={`${css.objectiveStatusTag} ${
                                isAchieved
                                  ? css.objectiveStatusCompleted
                                  : css.objectiveStatusPending
                              }`}
                            >
                              {isAchieved ? (
                                <FormattedMessage id="MyProgressPage.statusMasteredConfirmed" defaultMessage="Mastered" />
                              ) : (
                                <FormattedMessage id="MyProgressPage.statusInProgress" defaultMessage="In Progress" />
                              )}
                            </span>
                          </div>
                          <p className={css.objectiveDescription}>{objText}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={css.skillModalFooter}>
              <button
                type="button"
                className={css.skillModalCloseBtn}
                onClick={() => setSelectedSkill(null)}
              >
                <FormattedMessage id="MyProgressPage.closeBtn" defaultMessage="Close" />
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 3: Active Homework Assignment Details Modal
          ══════════════════════════════════════════════════════════ */}
      <Modal
        id="HomeworkDetailsModal"
        isOpen={isHomeworkModalOpen}
        onClose={() => setIsHomeworkModalOpen(false)}
        onManageDisableScrolling={handleManageDisableScrolling}
        usePortal
        containerClassName={css.homeworkModalContainer}
        contentClassName={css.modalContent}
      >
        <div dir={isArabic ? 'rtl' : 'ltr'}>
          <div className={css.homeworkModalHeader}>
            <div className={css.homeworkModalHeaderTop}>
              <span className={css.homeworkBadge}>
                <FormattedMessage
                  id="MyProgressPage.activeAssignmentBadge"
                  defaultMessage="View Assignment"
                />
              </span>
            </div>

            <h2 className={css.homeworkModalTitle}>
              <FormattedMessage id="MyProgressPage.homeworkModalTitle" />
            </h2>
            <p className={css.homeworkModalSubtitle}>
              <FormattedMessage id="MyProgressPage.homeworkModalSubtitle" />
            </p>
          </div>

          <div className={css.homeworkModalBody}>
            <div className={css.homeworkBox}>
              <h4 className={css.homeworkBoxHeading}>
                <FormattedMessage id="MyProgressPage.assignmentInstructions" />
              </h4>
              <p className={css.homeworkBoxText}>
                {currentHomework || <FormattedMessage id="MyProgressPage.noPendingAssignment" />}
              </p>
            </div>

            <div className={css.homeworkTipBox}>
              <IconCard type="learning_info" className={css.homeworkTipIcon} strokeWidth="2" />
              <span>
                <FormattedMessage id="MyProgressPage.homeworkTipText" />
              </span>
            </div>
          </div>

          <div className={css.homeworkModalFooter}>
            <button
              type="button"
              className={css.homeworkModalCloseBtn}
              onClick={() => setIsHomeworkModalOpen(false)}
            >
              <FormattedMessage id="MyProgressPage.closeBtn" />
            </button>

            <NamedLink
              name="SearchPage"
              className={css.homeworkModalActionBtn}
              onClick={() => setIsHomeworkModalOpen(false)}
            >
              <span>
                <FormattedMessage id="MyProgressPage.bookNextLessonBtn" />
              </span>
              <IconCard type="arrowRight" width="14" height="14" strokeWidth="2.5" />
            </NamedLink>
          </div>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 4: Permanent Level History & Audit Trail Modal
          ══════════════════════════════════════════════════════════ */}
      <Modal
        id="LevelHistoryModal"
        isOpen={isLevelHistoryModalOpen}
        onClose={() => {
          setIsLevelHistoryModalOpen(false);
          setSelectedMilestone(null);
        }}
        onManageDisableScrolling={handleManageDisableScrolling}
        usePortal
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        <div dir={isArabic ? 'rtl' : 'ltr'}>
          <div className={css.modalHeader}>
            <div className={css.modalHeaderTop}>
              <span className={`${css.reportTypeTag} ${css.reportTypeLesson}`}>
                <FormattedMessage id="MyProgressPage.auditTrailBadge" />
              </span>
              <span className={css.sectionBadge}>
                <FormattedMessage
                  id="MyProgressPage.levelMilestonesCount"
                  values={{ count: levelHistory.length }}
                />
              </span>
            </div>

            <h2 className={css.modalTitle}>
              <FormattedMessage id="MyProgressPage.levelHistoryTitle" />
            </h2>
            <p className={css.modalSubtitle}>
              <FormattedMessage id="MyProgressPage.levelHistorySubtitle" />
            </p>
          </div>

          <div className={css.modalBody}>
            {levelHistory.length === 0 ? (
              <div className={css.historyEmpty}>
                <FormattedMessage id="MyProgressPage.noLevelHistory" />
              </div>
            ) : (
              <div className={css.historyList}>
                {levelHistory.map((item, idx) => {
                  const dateStr = item.date
                    ? new Date(item.date).toLocaleDateString(isArabic ? 'ar-EG' : undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : null;

                  const isInitial =
                    item.confirmedBy === 'placement_questionnaire' ||
                    item.confirmedBy === 'placement' ||
                    !item.fromLevel ||
                    (item.reason &&
                      (item.reason.toLowerCase().includes('placement questionnaire') ||
                        item.reason.toLowerCase().includes('initial placement')));

                  const isHighlighted =
                    selectedMilestone &&
                    (selectedMilestone === item ||
                      (selectedMilestone.date === item.date &&
                        selectedMilestone.skill === item.skill &&
                        selectedMilestone.toLevel === item.toLevel));

                  return (
                    <div
                      key={idx}
                      className={`${css.historyItem} ${
                        isHighlighted ? css.historyItemHighlighted : ''
                      }`}
                    >
                      <div className={css.historyIcon}>
                        {renderSkillIcon(item.skill, css.historySkillSvg)}
                      </div>
                      <div className={css.historyDetails}>
                        <div className={css.historyTitleRow}>
                          <span className={css.historyTitle}>
                            {item.skill ? (
                              <FormattedMessage
                                id={`MyProgressPage.skill.${item.skill.toLowerCase()}`}
                                defaultMessage={item.skill}
                              />
                            ) : (
                              item.skill
                            )}
                            :{' '}
                            {item.fromLevel || (
                              <FormattedMessage id="MyProgressPage.initialLevel" />
                            )}{' '}
                            → {item.toLevel}
                          </span>
                          <div className={css.historyItemActions}>
                            {dateStr && <span className={css.historyDate}>{dateStr}</span>}
                            {!isInitial && (
                              <button
                                type="button"
                                className={css.downloadCertBtn}
                                onClick={e => {
                                  e.stopPropagation();
                                  const url = getCertificateDownloadUrl({
                                    certificateId: item.certificateId,
                                    childId: learningProfile?.childId,
                                    skill: item.skill,
                                    level: item.toLevel,
                                    locale: isArabic ? 'ar' : 'en',
                                  });
                                  window.open(url, '_blank');
                                }}
                                title={intl.formatMessage({
                                  id: 'MyProgressPage.downloadCertificate',
                                  defaultMessage: 'Download Certificate',
                                })}
                              >
                                <IconCard
                                  type="learning_award"
                                  width="13"
                                  height="13"
                                  strokeWidth="2.2"
                                />
                                <span>
                                  <FormattedMessage
                                    id="MyProgressPage.downloadCertificate"
                                    defaultMessage="Certificate"
                                  />
                                </span>
                              </button>
                            )}
                          </div>
                        </div>

                        {!isInitial && (
                          <span className={css.historyMeta}>
                            <FormattedMessage
                              id="MyProgressPage.confirmedByTutor"
                              values={{
                                tutor: item.tutorId ? `(${item.tutorId.substring(0, 8)}...)` : '',
                              }}
                            />
                            {item.reason ? ` • ${item.reason}` : ''}
                            {item.completedObjectives?.length > 0 && (
                              <>
                                {' • '}
                                <FormattedMessage
                                  id="MyProgressPage.objectivesCountMastered"
                                  values={{ count: item.completedObjectives.length }}
                                />
                              </>
                            )}
                          </span>
                        )}

                        {item.completedObjectives && item.completedObjectives.length > 0 && (
                          <div className={css.historyObjectivesChips}>
                            {item.completedObjectives.map((obj, cIdx) => {
                              const objId =
                                typeof obj === 'string' ? obj : obj?.objectiveId || obj?.id;
                              const curriculumObj = OBJECTIVE_MAP.get(objId);
                              const rawText = curriculumObj
                                ? isArabic
                                  ? curriculumObj.text_ar || curriculumObj.text_en
                                  : curriculumObj.text_en || curriculumObj.text_ar
                                : null;
                              const objText = formatObjectiveWithStudentName(
                                rawText,
                                learnerDisplayName,
                                isArabic
                              );

                              return (
                                <span
                                  key={cIdx}
                                  className={css.historyObjectiveChip}
                                  title={objText || objId}
                                >
                                  {objId}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={css.modalFooter}>
            <button
              type="button"
              className={css.modalCloseBtn}
              onClick={() => {
                setIsLevelHistoryModalOpen(false);
                setSelectedMilestone(null);
              }}
            >
              <FormattedMessage id="MyProgressPage.closeBtn" />
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

LearningProgressView.propTypes = {
  learnerName: PropTypes.string,
  learningProfile: PropTypes.object,
  skillBars: PropTypes.array,
  learningStats: PropTypes.object,
  timelineReports: PropTypes.array,
  timelineHasMore: PropTypes.bool,
  loadingTimeline: PropTypes.bool,
  onLoadMoreTimeline: PropTypes.func,
  onOpenPlacementModal: PropTypes.func,
  onManageDisableScrolling: PropTypes.func,
  showHeroCard: PropTypes.bool,
  showLevelHistory: PropTypes.bool,
  isParentView: PropTypes.bool,
  isIndependent: PropTypes.bool,
  childSwitcherSlot: PropTypes.node,
  user: PropTypes.object,
  className: PropTypes.string,
};

export default LearningProgressView;
